import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "../axiosConfig";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "../pages/CustomButton";
import Loader from "../dist/Loader";

const DIVISIONS_ENDPOINT = `${BASE_URL}/api/divisions`;
const STATES_ENDPOINT = `${BASE_URL}/api/states`;
const EMPLOYEES_ENDPOINT = `${BASE_URL}/api/employees`;
const PERMS_FETCH_ENDPOINT = `${BASE_URL}/api/get-employees-permisions`;
const BULK_PERM_ENDPOINT = `${BASE_URL}/api/employees-permissions-bulk`;
const DELETE_EMPLOYEE_ENDPOINT = `${BASE_URL}/api/employees`;

const COLUMNS = [
  "Add Area",
  "Add Route",
  "Add Doctor",
  "Edit Doctor",
  "Add Chemist",
  "Edit Chemist",
  "Add Stockist",
  "Edit Stockist",
  "Assign Doctor",
  "Assign Chemist",
  "Assign Stockist",
  "Add Institute",
  "Edit Institute",
  "Deviate TP",
  "Report Period",
  "Manager Expense Type",
  "Status",
  "Manager TP Type City",
];

/* permission keys matching backend payload order - snake_case */
const PERMISSION_KEYS = [
  "add_area",
  "add_route",
  "add_doctor",
  "edit_doctor",
  "add_chemist",
  "edit_chemist",
  "add_stockist",
  "edit_stockist",
  "assign_doctor",
  "assign_chemist",
  "assign_stockist",
  "add_institute",
  "edit_institute",
  "deviate_tp",
  "report_period",
  "manager_expense_type",
  "status",
  "manager_tp_type_city",
];

const makeDefaultPermission = () => {
  const out = {};
  PERMISSION_KEYS.forEach((k) => {
    out[k] = k === "report_period" ? 0 : false;
  });
  return out;
};

const normalizePerm = (p = {}) => {
  const out = {};
  PERMISSION_KEYS.forEach((k) => {
    if (k === "report_period") {
      const n = typeof p[k] === "number" ? p[k] : Number(p[k]);
      out[k] = Number.isFinite(n) ? n : 0;
    } else {
      out[k] = !!p[k];
    }
  });
  return out;
};

const EmployeeRights = () => {
  const [divisions, setDivisions] = useState([]);
  const [states, setStates] = useState([]);
  const [selectedDivision, setSelectedDivision] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bulkValues, setBulkValues] = useState(() => makeDefaultPermission());
  const [showTable, setShowTable] = useState(false);

  const columns = useMemo(() => COLUMNS, []);

  useEffect(() => {
    let mounted = true;
    const fetchMeta = async () => {
      try {
        const [dRes, sRes] = await Promise.all([
          axios.get(DIVISIONS_ENDPOINT, { headers: getAuthHeaders() }),
          axios.get(STATES_ENDPOINT, { headers: getAuthHeaders() }),
        ]);
        if (!mounted) return;
        setDivisions(dRes?.data ?? []);
        setStates(sRes?.data ?? []);
      } catch (err) {
        console.error("Failed to load divisions/states", err);
      }
    };
    fetchMeta();
    return () => {
      mounted = false;
    };
  }, []);

  const fetchEmployees = useCallback(async () => {
    if (!selectedDivision || !selectedState) {
      alert("Please select Division and State first");
      return;
    }
    setLoading(true);
    try {
      const empRes = await axios.get(EMPLOYEES_ENDPOINT, {
        headers: getAuthHeaders(),
        params: {
          division_id: selectedDivision,
          state_id: selectedState,
        },
      });
      const employees = Array.isArray(empRes.data) ? empRes.data : [];

      const normalizedRows = employees.map((emp, idx) => {
        const id = emp.id ?? emp.employee_id ?? idx;
        const fullName = `${emp.first_name ?? ""} ${
          emp.last_name ?? ""
        }`.trim();
        return {
          raw: emp,
          id,
          full_name: fullName,
          division: emp.division_name ?? emp.division ?? selectedDivision,
          state: emp.state_name ?? emp.state ?? selectedState,
          permissions: null, // to be filled
          isEditing: false,
        };
      });

      setRows(normalizedRows);

      const employeeIds = normalizedRows
        .map((r) => r.id)
        .filter((v) => v !== undefined && v !== null);
      if (employeeIds.length === 0) {
        setLoading(false);
        return;
      }

      const permRes = await axios.get(PERMS_FETCH_ENDPOINT, {
        params: { employee_ids: employeeIds },
        headers: getAuthHeaders(),
      });

      const permData = Array.isArray(permRes.data)
        ? permRes.data
        : permRes.data?.permissions ?? [];

      const permMap = {};
      permData.forEach((p) => {
        const id = p.employee_id ?? p.id ?? p.emp_id;
        if (id !== undefined && id !== null)
          permMap[String(id)] = normalizePerm(p);
      });

      const rowsWithPerms = normalizedRows.map((r) => ({
        ...r,
        permissions: permMap[String(r.id)] ?? makeDefaultPermission(),
      }));

      setRows(rowsWithPerms);
      setShowTable(true);
    } catch (err) {
      console.error("Failed to fetch employees or permissions", err);
      alert("Failed to fetch employees. See console for details.");
    } finally {
      setLoading(false);
    }
  }, [selectedDivision, selectedState]);

  const togglePermission = useCallback((rowIndex, key) => {
    setRows((prev) => {
      const next = prev.map((r, idx) => {
        if (idx !== rowIndex) return r;
        const perms = { ...(r.permissions ?? makeDefaultPermission()) };
        if (key === "report_period") {
          return r;
        }
        perms[key] = !perms[key];
        return { ...r, permissions: perms };
      });
      return next;
    });
  }, []);

  /* update numeric report_period */
  const updateReportPeriod = useCallback((rowIndex, value) => {
    const parsed = Number(value) || 0;
    setRows((prev) =>
      prev.map((r, idx) =>
        idx === rowIndex
          ? {
              ...r,
              permissions: {
                ...(r.permissions ?? makeDefaultPermission()),
                report_period: parsed,
              },
            }
          : r
      )
    );
  }, []);

  /* create payload for single or bulk update - uses permissions obj and employee_ids array */
  const createPayload = (employeeIds, permissions) => {
    const payload = { employee_ids: employeeIds };
    PERMISSION_KEYS.forEach((k) => {
      payload[k] =
        k === "report_period" ? Number(permissions[k]) || 0 : !!permissions[k];
    });
    return payload;
  };

  const onEditClick = useCallback(
    async (rowIndex) => {
      const row = rows[rowIndex];
      if (!row) return;

      if (row.isEditing) {
        // Save
        const payload = createPayload(
          [row.id],
          row.permissions ?? makeDefaultPermission()
        );
        setLoading(true);
        try {
          await axios.post(BULK_PERM_ENDPOINT, payload, {
            headers: getAuthHeaders(),
          });
          setRows((prev) =>
            prev.map((r, idx) =>
              idx === rowIndex ? { ...r, isEditing: false } : r
            )
          );
        } catch (err) {
          console.error("Failed to update permissions", err);
          alert("Failed to save permissions. See console for details.");
        } finally {
          setLoading(false);
        }
      } else {
        // Enable editing
        setRows((prev) =>
          prev.map((r, idx) =>
            idx === rowIndex ? { ...r, isEditing: true } : r
          )
        );
      }
    },
    [rows]
  );

  const onDelete = async (rowIndex) => {
    const row = rows[rowIndex];
    if (!row) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete ${row.full_name}?`
    );
    if (!confirmed) return;

    try {
      await axios.delete(`${BASE_URL}/api/permissions/delete`, {
        data: { employee_ids: [row.id] },
        headers: getAuthHeaders(),
      });

      setRows((prev) => prev.filter((_, idx) => idx !== rowIndex));

      alert(`${row.full_name} deleted successfully`);
    } catch (err) {
      console.error("Failed to delete employee", err);
      alert("Failed to delete employee. See console for details.");
    }
  };

  const handleBulkChange = useCallback((key, value) => {
    setBulkValues((prev) => ({
      ...prev,
      [key]: key === "report_period" ? Number(value) || 0 : !!value,
    }));
  }, []);

  const handleBulkUpdate = useCallback(
    async (key) => {
      if (rows.length === 0) {
        alert("No employees to update");
        return;
      }
      const employeeIds = rows.map((r) => r.id);
      const baseline = rows[0]?.permissions ?? makeDefaultPermission();
      const permissions = {
        ...baseline,
        [key]:
          key === "report_period"
            ? Number(bulkValues[key]) || 0
            : !!bulkValues[key],
      };

      const payload = createPayload(employeeIds, permissions);
      setLoading(true);
      try {
        await axios.post(BULK_PERM_ENDPOINT, payload, {
          headers: getAuthHeaders(),
        });
        setRows((prev) =>
          prev.map((r) => ({
            ...r,
            permissions: {
              ...(r.permissions ?? makeDefaultPermission()),
              [key]: permissions[key],
            },
          }))
        );
        alert(`Successfully updated ${key} for ${rows.length} employees`);
      } catch (err) {
        console.error("Failed to update permissions in bulk", err);
        alert("Failed to update permissions. See console for details.");
      } finally {
        setLoading(false);
      }
    },
    [rows, bulkValues]
  );

  return (
    <div className="bg-gray-100 font-sans text-sm">
      <div className=" bg-white border border-gray-300 shadow-sm">
        <div className="border-b border-gray-300 p-2">
          <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
            <span className="text-blue-700"> Employee </span>
            <span className="text-blue-500">Rights</span>
          </h1>
        </div>

        <div className="border border-gray-300 bg-white p-4 rounded-sm">
          <div className="flex justify-center items-center gap-6">
            {/* State Dropdown */}
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600">State:</label>
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="border border-gray-300 p-2 rounded-sm bg-white w-44"
              >
                <option value="">Select State</option>
                {states.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Division Dropdown */}
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600">Division:</label>
              <select
                value={selectedDivision}
                onChange={(e) => setSelectedDivision(e.target.value)}
                className="border border-gray-300 p-2 rounded-sm bg-white w-44"
              >
                <option value="">Select Division</option>
                {divisions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            {/* GO Button */}
            <div className="flex items-center mt-1">
              <CustomButton
                label={loading ? "Loading..." : "GO"}
                variant="go"
                onClick={fetchEmployees}
                className="px-6 py-2"
                disabled={loading || !selectedDivision || !selectedState}
              />
            </div>
          </div>
        </div>

        {showTable && (
          <div className="border rounded shadow table-wrapper mt-5">
            <table className="unlock-dcr-leave-table text-sm ">
              <thead className="bg-blue-500 text-white">
                <tr className="bg-cyan-100 border-t border-b border-gray-300">
                  <th className="py-3 px-1 text-left w-6 sticky top-0 z-30 bg-cyan-100">
                    S.No
                  </th>
                  <th className="py-3 px-1 text-left w-12 sticky top-0 z-30 bg-cyan-100">
                    Employee
                  </th>
                  <th className="py-3 px-1 text-left w-12 sticky top-0 z-30 bg-cyan-100">
                    Division
                  </th>
                  <th className="py-3 px-1 text-left w-12 sticky top-0 z-30 bg-cyan-100">
                    State
                  </th>
                  {columns.map((col, idx) => (
                    <th
                      key={idx}
                      className="py-3 px-1 text-center min-w-[40px] sticky top-0 z-30 bg-cyan-100"
                    >
                      <div className="text-xs font-semibold">{col}</div>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={r.id}
                    className="even:bg-white odd:bg-white border-b border-gray-200"
                  >
                    <td className="p-2 align-top">
                      <div className="flex flex-col gap-1">
                        <CustomButton
                          variant="edit"
                          onClick={() => onEditClick(i)}
                        >
                          {r.isEditing ? "SAVE" : "EDIT"}
                        </CustomButton>
                        <CustomButton
                          variant="delete"
                          onClick={() => onDelete(i)}
                        >
                          Delete
                        </CustomButton>
                      </div>
                    </td>
                    <td className="p-2 align-top">
                      <div className="font-semibold">{`${r.full_name}`}</div>
                      {/* <div className="text-[10px] text-gray-500 mt-1">
                      {`${r.employee_username ?? ""}`}
                    </div> */}
                    </td>

                    <td className="p-2 align-top">{r.division}</td>
                    <td className="p-2 align-top">{r.state}</td>

                    {PERMISSION_KEYS.map((key) => (
                      <td key={key} className="p-2 align-top text-center">
                        {key === "report_period" ? (
                          r.isEditing ? (
                            <input
                              type="number"
                              value={r.permissions?.report_period ?? 0}
                              onChange={(e) =>
                                updateReportPeriod(i, e.target.value)
                              }
                              className="w-10 p-1 border rounded text-center"
                            />
                          ) : (
                            <div className="flex items-center justify-center">
                              <div className="text-[10px] font-semibold mr-2">
                                {r.permissions?.report_period ?? 0}
                              </div>
                            </div>
                          )
                        ) : (
                          <input
                            type="checkbox"
                            checked={!!r.permissions?.[key]}
                            onChange={() => togglePermission(i, key)}
                            disabled={!r.isEditing}
                            className="w-4 h-4"
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}

                {/* bulk row */}
                <tr className="bg-slate-600 text-white">
                  <td colSpan={4} className="p-2 align-top">
                    &nbsp;
                  </td>
                  {PERMISSION_KEYS.map((key, idx) => (
                    <td key={key} className="p-2 align-top text-center">
                      <div className="flex flex-col items-center gap-1">
                        {key === "report_period" ? (
                          <input
                            type="number"
                            value={bulkValues.report_period ?? 0}
                            onChange={(e) =>
                              handleBulkChange(key, e.target.value)
                            }
                            className="w-10 p-1 border rounded text-center"
                          />
                        ) : (
                          <input
                            type="checkbox"
                            checked={!!bulkValues[key]}
                            onChange={(e) =>
                              handleBulkChange(key, e.target.checked)
                            }
                            className="w-4 h-4"
                          />
                        )}
                        <CustomButton
                          className="mt-1 px-2 py-1 bg-green-600 text-white rounded text-[10px] font-semibold border"
                          onClick={() => handleBulkUpdate(key)}
                        >
                          UPDATE
                        </CustomButton>
                      </div>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
      {loading && <Loader onCancel={() => setLoading(false)} />}
    </div>
  );
};

export default EmployeeRights;
